import { useParams } from 'react-router-dom';
import { Chips, CustomButton, LoadingSpinner } from '@medtronic/pecc-react-component-library-js';
import { Box, Typography } from '@mui/material';
import { capitalize } from 'lodash';
import { EmrStatusEnum } from '~/api/types';
import { useCreateEmrMutation, useEditEmrMutation, useGetEmrsQuery, useGetProjectQuery } from '~/api/v2/hooks';
import { Tooltip } from '~/components';
import { ProjectActionTypeEnum, useProjectActionState } from '~/hooks';
import { DocumentIcon, EditIcon } from '~/icons';
import { useAppDispatch, useAppSelector } from '~/redux';
import { selectCurrentUserApplicationRole, selectCurrentUserId } from '~/redux/slices/currentUserSlice';
import { ModalTypesEnum, openModal } from '~/redux/slices/modalSlice';
import { getPriviligies } from '~/utils';
import { CreationDisabledChip, EmrDetails } from '.';
import { createBlockContainerStyles, createBlockStyles } from './styles';

const EmrStudioBlock = () => {
  const projectId = +useParams().projectid!;
  const userId = useAppSelector(selectCurrentUserId);
  const dispatch = useAppDispatch();
  const userApplicationRole = useAppSelector(selectCurrentUserApplicationRole);
  const { data, isFetching } = useGetEmrsQuery({ projectId });
  const [, { isLoading }] = useCreateEmrMutation({
    fixedCacheKey: `create-emr-project-${projectId}`,
  });
  const [, { isLoading: isUpdating }] = useEditEmrMutation({
    fixedCacheKey: `edit-emr-project-${projectId}`,
  });
  const { data: project } = useGetProjectQuery({ projectId });

  const inProgress = isLoading || isFetching || isUpdating;

  const { isDisabledAction: isStudioCreationDisabled, tooltipMessage: studioCreationMessage } = useProjectActionState({
    actionType: ProjectActionTypeEnum.EMR_STUDIO,
  });
  const userProjectRole = project?.members?.find(({ id }) => id === userId)?.role_id;
  const userPriviligies = getPriviligies(userProjectRole) || getPriviligies(userApplicationRole);
  const isEditable = !!data?.id && !inProgress && userPriviligies?.studioIntegrationTab.canEdit;

  return (
    <Box
      sx={{
        ...createBlockContainerStyles,
        minHeight: data?.id && !inProgress ? 'fit-content' : '50vh',
      }}
    >
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" gap="2rem" alignItems="center">
          <Typography variant="h5">EMR Studio</Typography>
          {isEditable && (
            <Tooltip title={studioCreationMessage} disableHoverListener={!isStudioCreationDisabled}>
              <CustomButton
                onClick={() => dispatch(openModal({ type: ModalTypesEnum.CREATE_EMR_STUDIO }))}
                buttonType="borderless"
                disabled={isStudioCreationDisabled}
                label={
                  <Box>
                    <EditIcon /> Edit
                  </Box>
                }
              />
            </Tooltip>
          )}
          <Tooltip
            disableHoverListener={data?.status !== EmrStatusEnum.PENDING}
            title="Your request is being processed. You will be notified when it is complete."
          >
            <span>
              {!!data && !inProgress && (
                <Chips
                  chipsConfig={[
                    {
                      label: capitalize(data?.status),
                      type: 'customSolid',
                      customColor: data?.status === EmrStatusEnum.ACTIVE ? '#DFF4C8' : '#C2D5FF',
                    },
                  ]}
                  alignment="left"
                />
              )}
            </span>
          </Tooltip>
        </Box>
        {isEditable && (
          <Tooltip title={studioCreationMessage} disableHoverListener={!isStudioCreationDisabled}>
            <CustomButton
              disabled={isStudioCreationDisabled || data?.status !== EmrStatusEnum.ACTIVE}
              buttonType="outlined"
              onClick={() => {
                if (data?.url) {
                  window.open(data.url, '_blank', 'noopener,noreferrer');
                }
              }}
              label="Navigate to EMR Studio"
            />
          </Tooltip>
        )}
      </Box>
      {inProgress ? (
        <Box display="flex" justifyContent="center" my="15vh">
          <LoadingSpinner />
        </Box>
      ) : data ? (
        <EmrDetails />
      ) : (
        <Box sx={createBlockStyles}>
          <DocumentIcon fill="#0000004D" />
          <Typography variant="body1" color="#0000004D">
            No Studios to display
          </Typography>
          {userPriviligies?.studioIntegrationTab.canEdit && (
            <Tooltip disableHoverListener={!isStudioCreationDisabled} title={studioCreationMessage}>
              <span>
                <CustomButton
                  label="Create Studio"
                  disabled={isStudioCreationDisabled}
                  onClick={() => {
                    dispatch(openModal({ type: ModalTypesEnum.CREATE_EMR_STUDIO }));
                  }}
                />
              </span>
            </Tooltip>
          )}
          {isStudioCreationDisabled && (
            <CreationDisabledChip title="Studio creation disabled: " description={studioCreationMessage} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default EmrStudioBlock;
